/*
 * <nl-w2-reels [data-autoplay] data-label-play data-label-pause> (sections/nl-w2-reels.liquid)
 *   [data-reel] > video[data-reel-video][data-src] + button[data-reel-toggle] + [data-reel-bar]
 *
 * Video loading and playback policy:
 *   - no src until the card is actually visible (IntersectionObserver also respects the rail's horizontal clipping)
 *   - autoplay muted + inline + loop only while at least 55 % visible, pause when it leaves, when the tab is hidden
 *     and when the element disconnects; never autoplay under reduced motion, the theme motion setting or Save-Data
 *   - the toggle always works (user intent wins until the card leaves the viewport and comes back)
 *   - card states: data-state="playing|paused", data-started once the first frame plays (poster cross fade in CSS)
 *   - playback bar: one rAF loop while anything plays, transform only
 */
(() => {
  'use strict';

  if (customElements.get('nl-w2-reels')) return;

  const motionOff = () =>
    (window.NL && window.NL.motion && window.NL.motion.reduced) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const saveData = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return Boolean(connection && connection.saveData);
  };

  class W2Reels extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.labels = {
        play: this.dataset.labelPlay || 'Play',
        pause: this.dataset.labelPause || 'Pause',
      };
      this.items = [];

      this.querySelectorAll('[data-reel]').forEach((card) => {
        const video = card.querySelector('[data-reel-video]');
        if (!video) return;
        const item = {
          card,
          video,
          toggle: card.querySelector('[data-reel-toggle]'),
          bar: card.querySelector('[data-reel-bar]'),
          visible: false,
          loaded: false,
          userPaused: false,
          userPlayed: false,
        };
        video.muted = true;
        video.addEventListener('playing', () => {
          card.setAttribute('data-started', '');
          this.setState(item, 'playing');
          this.startTicker();
        });
        video.addEventListener('pause', () => this.setState(item, 'paused'));
        video.addEventListener('error', () => {
          // polish-v3 C3: derived SD-480p URL failed, fall back to the rendition Liquid listed
          const fallback = video.dataset.srcFallback;
          if (fallback && video.getAttribute('src') !== fallback) {
            video.src = fallback;
            if (item.visible && !document.hidden) this.play(item);
            return;
          }
          this.setState(item, 'paused');
        });
        if (item.toggle) item.toggle.addEventListener('click', () => this.toggle(item));
        this.items.push(item);
      });

      // Theme editor: selecting a card block brings it into view inside the rail
      this.onBlockSelect = (event) => {
        const item = event.target && event.target.closest ? event.target.closest('.nl-rail__item') : null;
        const track = item ? item.parentElement : null;
        if (!item || !track) return;
        const padding = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        const delta = item.getBoundingClientRect().left - track.getBoundingClientRect().left - padding;
        track.scrollBy({ left: delta, behavior: motionOff() ? 'auto' : 'smooth' });
      };
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      // fix-home-1 (QA v3): keyboard focus inside a card that is clipped by the rail brings the card fully into view,
      // so the focus ring is never off screen (only the rail scrolls sideways, the page does not jump)
      this.onFocusIn = (event) => {
        const item = event.target && event.target.closest ? event.target.closest('.nl-rail__item') : null;
        const track = item ? item.parentElement : null;
        if (!item || !track || !this.contains(track)) return;
        const t = track.getBoundingClientRect();
        const r = item.getBoundingClientRect();
        const padding = parseFloat(getComputedStyle(track).scrollPaddingLeft) || parseFloat(getComputedStyle(track).paddingLeft) || 0;
        let delta = 0;
        if (r.left < t.left + padding) delta = r.left - t.left - padding;
        else if (r.right > t.right) delta = r.right - t.right + 8;
        if (Math.abs(delta) > 1) track.scrollBy({ left: delta, behavior: motionOff() ? 'auto' : 'smooth' });
      };
      this.addEventListener('focusin', this.onFocusIn);

      if (!this.items.length) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const item = this.items.find((candidate) => candidate.card === entry.target);
              if (!item) return;
              const wasVisible = item.visible;
              item.visible = entry.isIntersecting && entry.intersectionRatio >= 0.55;
              // Leaving the viewport resets user intent, so autoplay rules apply again next time
              if (wasVisible && !item.visible) {
                item.userPaused = false;
                item.userPlayed = false;
              }
            });
            this.evaluateAll();
          },
          { threshold: [0, 0.55, 0.9] }
        );
        this.items.forEach((item) => this.observer.observe(item.card));
      }

      this.onVisibility = () => this.evaluateAll();
      document.addEventListener('visibilitychange', this.onVisibility);
      // polish-v3 C3: only the card nearest to the middle of the rail autoplays; re-pick after the rail settles
      this.track = this.querySelector('[data-rail-track]');
      if (this.track) {
        this.onTrackScroll = () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.evaluateAll(), 160);
        };
        this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.onBlockSelect) this.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onFocusIn) this.removeEventListener('focusin', this.onFocusIn);
      if (this.track && this.onTrackScroll) this.track.removeEventListener('scroll', this.onTrackScroll);
      clearTimeout(this.scrollTimer);
      if (this.tickFrame) cancelAnimationFrame(this.tickFrame);
      this.tickFrame = null;
      (this.items || []).forEach((item) => item.video.pause());
      this._bound = false;
    }

    autoplayAllowed() {
      return this.hasAttribute('data-autoplay') && !motionOff() && !saveData();
    }

    load(item) {
      if (item.loaded) return;
      const src = item.video.dataset.src;
      if (!src) return;
      item.loaded = true;
      item.video.src = src;
    }

    pickLead() {
      const visible = this.items.filter((item) => item.visible);
      this.lead = null;
      if (!visible.length) return;
      const box = this.track ? this.track.getBoundingClientRect() : { left: 0, width: window.innerWidth };
      const middle = box.left + box.width / 2;
      let best = Infinity;
      visible.forEach((item) => {
        const r = item.card.getBoundingClientRect();
        const distance = Math.abs(r.left + r.width / 2 - middle);
        if (distance < best) {
          best = distance;
          this.lead = item;
        }
      });
    }
    evaluateAll() {
      this.pickLead();
      this.items.forEach((item) => this.evaluate(item));
    }
    evaluate(item) {
      const wanted = item.userPlayed || (this.autoplayAllowed() && !item.userPaused && item === this.lead);
      if (item.visible && !document.hidden && wanted) {
        this.play(item);
      } else if (!item.video.paused) {
        item.video.pause();
      }
    }

    play(item) {
      this.load(item);
      const promise = item.video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => this.setState(item, 'paused'));
      }
    }

    toggle(item) {
      if (item.video.paused) {
        item.userPaused = false;
        item.userPlayed = true;
        this.play(item);
      } else {
        item.userPaused = true;
        item.userPlayed = false;
        item.video.pause();
      }
    }

    setState(item, state) {
      item.card.setAttribute('data-state', state);
      if (item.toggle) item.toggle.setAttribute('aria-label', state === 'playing' ? this.labels.pause : this.labels.play);
    }

    startTicker() {
      if (this.tickFrame) return;
      const tick = () => {
        let running = false;
        this.items.forEach((item) => {
          if (item.video.paused || !item.bar) return;
          running = true;
          const duration = item.video.duration;
          if (!duration || !Number.isFinite(duration)) return;
          item.bar.style.transform = `scaleX(${(item.video.currentTime / duration).toFixed(4)})`;
        });
        this.tickFrame = running ? requestAnimationFrame(tick) : null;
      };
      this.tickFrame = requestAnimationFrame(tick);
    }
  }

  customElements.define('nl-w2-reels', W2Reels);
})();
